'use client';

import { RefObject, useEffect, useRef } from 'react';
import gsap from 'gsap';

/* Photon — interactive glow effect for the Wavelengths bento.
   Adapted from the React Bits MagicBento `enableSpotlight` +
   `enableBorderGlow` features. Stripped of the particle/tilt/magnetism
   features so the effect is calm and product-appropriate. Red glow
   tuned to match the page's existing 660 nm beam (#ff0202 ≈ 255,2,2).

   The per-card glow ring CSS (formerly the `photonGlowCardCss` styled
   mixin) now lives inline in WavelengthsSection.module.css on the card
   classes this watcher targets via `[data-magic-card]`. */

export const PHOTON_GLOW_COLOR = '255, 2, 2';
const DEFAULT_SPOTLIGHT_RADIUS = 360;
const MOBILE_BREAKPOINT = 768;

interface PhotonGlobalSpotlightProps {
  /** Element that owns the bento. Spotlight is only active while the
   *  cursor is inside this element's bounds. */
  sectionRef: RefObject<HTMLElement | null>;
  /** Selector matched against descendants of the section to find the
   *  cards. Each match gets its glow CSS variables updated. */
  cardSelector?: string;
  /** RGB triple for the glow (no rgba wrapper). Defaults to Photon red. */
  glowColor?: string;
  /** Effective spotlight reach, in pixels. */
  spotlightRadius?: number;
}

/* Mounts a floating spotlight under document.body that follows the
   cursor, plus a per-card glow variable updater. Mobile is skipped —
   the effect is hover-driven and adds nothing on a touch device. */
export const PhotonGlobalSpotlight = ({
  sectionRef,
  cardSelector = '[data-magic-card]',
  glowColor = PHOTON_GLOW_COLOR,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
}: PhotonGlobalSpotlightProps) => {
  const spotlightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth <= MOBILE_BREAKPOINT) return;

    const section = sectionRef.current;
    if (!section) return;

    /* Floating circular gradient that follows the cursor. Lives on
       `document.body` so it isn't clipped by section overflow rules. */
    const spotlight = document.createElement('div');
    spotlight.setAttribute('aria-hidden', 'true');
    spotlight.style.cssText = `
      position: fixed;
      width: 800px;
      height: 800px;
      border-radius: 50%;
      pointer-events: none;
      background: radial-gradient(circle,
        rgba(${glowColor}, 0.16) 0%,
        rgba(${glowColor}, 0.08) 15%,
        rgba(${glowColor}, 0.04) 25%,
        rgba(${glowColor}, 0.02) 40%,
        rgba(${glowColor}, 0.01) 65%,
        transparent 70%
      );
      z-index: 1;
      opacity: 0;
      transform: translate(-50%, -50%);
      mix-blend-mode: screen;
    `;
    document.body.appendChild(spotlight);
    spotlightRef.current = spotlight;

    const proximity = spotlightRadius * 0.5;
    const fadeDistance = spotlightRadius * 0.75;

    const clearGlows = (cards: NodeListOf<HTMLElement>) => {
      cards.forEach((card) =>
        card.style.setProperty('--glow-intensity', '0'),
      );
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = section.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;

      const cards = section.querySelectorAll<HTMLElement>(cardSelector);

      if (!inside) {
        gsap.to(spotlight, {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.out',
        });
        clearGlows(cards);
        return;
      }

      let minDistance = Infinity;
      cards.forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cx = cardRect.left + cardRect.width / 2;
        const cy = cardRect.top + cardRect.height / 2;
        const dist =
          Math.hypot(e.clientX - cx, e.clientY - cy) -
          Math.max(cardRect.width, cardRect.height) / 2;
        const eff = Math.max(0, dist);
        minDistance = Math.min(minDistance, eff);

        let intensity = 0;
        if (eff <= proximity) intensity = 1;
        else if (eff <= fadeDistance)
          intensity = (fadeDistance - eff) / (fadeDistance - proximity);

        const relX = ((e.clientX - cardRect.left) / cardRect.width) * 100;
        const relY = ((e.clientY - cardRect.top) / cardRect.height) * 100;
        card.style.setProperty('--glow-x', `${relX}%`);
        card.style.setProperty('--glow-y', `${relY}%`);
        card.style.setProperty('--glow-intensity', intensity.toString());
        card.style.setProperty('--glow-radius', `${spotlightRadius}px`);
      });

      gsap.to(spotlight, {
        left: e.clientX,
        top: e.clientY,
        duration: 0.1,
        ease: 'power2.out',
      });

      const targetOpacity =
        minDistance <= proximity
          ? 0.8
          : minDistance <= fadeDistance
          ? ((fadeDistance - minDistance) / (fadeDistance - proximity)) * 0.8
          : 0;

      gsap.to(spotlight, {
        opacity: targetOpacity,
        duration: targetOpacity > 0 ? 0.2 : 0.5,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(spotlight, { opacity: 0, duration: 0.3, ease: 'power2.out' });
      clearGlows(section.querySelectorAll<HTMLElement>(cardSelector));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      spotlight.remove();
      spotlightRef.current = null;
    };
  }, [sectionRef, cardSelector, glowColor, spotlightRadius]);

  return null;
};
